'use client';

import React, {
    type FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { SearchBar } from '@paypoteam/ui-components';

import { date } from '@/lib/date';
import { trackSearchTermShopEvent } from '@/lib/gtm';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';
import { getCategoriesData, getPartnersData } from '@/services/paypo-content';

import { ContainerHeaderText } from '@/components/atoms/ContainerHeaderText';
import { Skeleton } from '@/components/atoms/Skeleton';
import H2 from '@/components/atoms/Typography/H2';
import type { ContentTextProps } from '@/components/molecules/ContentText';
import { ContentText } from '@/components/molecules/ContentText';
import Section from '@/components/layout/Section';

import CategoryButtons from './components/CategoryButtons';
import CategoryButtonsSlider from './components/CategoryButtonsSlider';
import TilesGrid from './components/TilesGrid';
import { mapAllPartnersList } from './utils/mapAllPartnersList';
import type {
    CategoriesDataType,
    PartnersDataType,
    SectionShopSearchProps,
} from './SectionShopSearch.types';

import { env } from '@/env.mjs';

const SectionShopSearch: FC<SectionShopSearchProps> = ({
    config,
    categoryName = '',
    groupName = '',
    descriptionData,
    showFilterNew,
    shopLinkPlacement,
    noDataLoadOnRender = false,
    shopsTilesLoading,
    isSectionVisible = true,
    ...props
}) => {
    const {
        title,
        id,
        ctaText,
        searchBarPlaceholder,
        searchBarHeaderText,
        'data-test-id': dataTestId,
        listMessages,
    } = config;

    const PAGE_LIMIT = 30;
    const pageCounter = useRef(2);

    const [isNewShopsFilterActive, setIsNewShopsFilterActive] = useState(false);
    const [isShopListEmpty, setIsShopListEmpty] = useState(noDataLoadOnRender);

    const [currentSearchText, setCurrentSearchText] = useState('');

    const [isPartnersListLoading, setIsPartnersListLoading] = useState(true);
    const [partnersListData, setPartnersListData] = useState<
        PartnersDataType[]
    >([]);
    const [partnersListCounter, setPartnersListCounter] = useState(0);

    const [categoriesData, setCategoriesData] = useState<CategoriesDataType[]>(
        [],
    );
    const [emptyListMessage, setEmptyListMessage] = useState(
        noDataLoadOnRender ? listMessages?.search : listMessages?.noResult,
    );

    const checkDateFrom = useCallback(
        () =>
            isNewShopsFilterActive
                ? date().subtract(1, 'month').format('YYYY-MM-DD')
                : null,
        [isNewShopsFilterActive],
    );

    const checkDateTo = useCallback(
        () => (isNewShopsFilterActive ? date().format('YYYY-MM-DD') : null),
        [isNewShopsFilterActive],
    );

    const handlePartnersLoad = () => {
        getPartnersData({
            page: pageCounter.current,
            limit: PAGE_LIMIT,
            categoryName,
            dateFrom: checkDateFrom(),
            dateTo: checkDateTo(),
            queryString: currentSearchText,
        }).then(response => {
            pageCounter.current += 1;
            const newPartners = mapAllPartnersList(response.items);

            setPartnersListData(prevPartners => [
                ...prevPartners,
                ...newPartners,
            ]);
        });
    };

    const resetPartnersList = useCallback(() => {
        setPartnersListData([]);
        setPartnersListCounter(0);
        setIsShopListEmpty(true);
        setEmptyListMessage(listMessages?.search);
    }, [listMessages?.search]);

    useEffect(() => {
        const handleSearch = (searchText: string) => {
            trackSearchTermShopEvent(searchText);

            pageCounter.current = 2;

            if (noDataLoadOnRender && !isNewShopsFilterActive && !searchText) {
                resetPartnersList();
            } else {
                getPartnersData({
                    page: 1,
                    limit: PAGE_LIMIT,
                    categoryName,
                    queryString: searchText,
                    dateFrom: checkDateFrom(),
                    dateTo: checkDateTo(),
                }).then(response => {
                    const mappedPartners = mapAllPartnersList(response.items);
                    setPartnersListData(mappedPartners);
                    setPartnersListCounter(response.totalCount);
                    setIsShopListEmpty(!mappedPartners.length);
                    setEmptyListMessage(listMessages?.noResult);
                });
            }
        };

        const shouldSearchByString =
            currentSearchText.length === 0 || currentSearchText.length > 1;

        if (!isPartnersListLoading && shouldSearchByString) {
            handleSearch(currentSearchText);
        }
    }, [
        listMessages?.noResult,
        listMessages?.search,
        categoryName,
        checkDateFrom,
        checkDateTo,
        currentSearchText,
        isNewShopsFilterActive,
        isPartnersListLoading,
        noDataLoadOnRender,
        resetPartnersList,
    ]);

    useEffect(() => {
        const handleDateFilter = () => {
            pageCounter.current = 2;

            if (noDataLoadOnRender && !isNewShopsFilterActive) {
                resetPartnersList();
            } else {
                getPartnersData({
                    page: 1,
                    limit: PAGE_LIMIT,
                    categoryName,
                    queryString: currentSearchText,
                    dateFrom: checkDateFrom(),
                    dateTo: checkDateTo(),
                    orderBy: isNewShopsFilterActive
                        ? 'firstPublicationDate'
                        : '',
                    orderDirection: isNewShopsFilterActive ? 'desc' : '',
                }).then(response => {
                    setPartnersListData(mapAllPartnersList(response.items));
                    setPartnersListCounter(response.totalCount);
                });
            }
        };

        noDataLoadOnRender && setIsShopListEmpty(!isNewShopsFilterActive);
        !isPartnersListLoading && handleDateFilter();
    }, [
        categoryName,
        checkDateFrom,
        checkDateTo,
        currentSearchText,
        isNewShopsFilterActive,
        isPartnersListLoading,
        noDataLoadOnRender,
        resetPartnersList,
    ]);

    useEffect(() => {
        getCategoriesData({
            groupName,
            categoryName,
        }).then(({ items }) => {
            setCategoriesData(items);
            noDataLoadOnRender && setIsPartnersListLoading(false);
        });

        !noDataLoadOnRender &&
            getPartnersData({
                page: 1,
                limit: PAGE_LIMIT,
                categoryName,
            }).then(response => {
                setIsPartnersListLoading(false);
                setPartnersListData(mapAllPartnersList(response.items));
                setPartnersListCounter(response.totalCount);
            });
    }, [categoryName, groupName, noDataLoadOnRender]);

    const customNewShopsFilter = {
        title: env.NEXT_PUBLIC_APP_COUNTRY === 'RO' ? 'Nou' : 'Nowe',
        isActive: isNewShopsFilterActive,
        handleClick: () => setIsNewShopsFilterActive(!isNewShopsFilterActive),
    };

    const shouldContentTextRender =
        !!descriptionData?.description?.length ||
        !!descriptionData?.title ||
        !!descriptionData?.subtitle;

    const { minWidthMd } = useCssDimensions();

    return isSectionVisible ? (
        <Section
            id={props.id || id}
            {...props}
        >
            {!!title && (
                <H2
                    className="heading-spaces text-center"
                    data-test-id={dataTestId && `${dataTestId}-title`}
                >
                    {title}
                </H2>
            )}

            {isPartnersListLoading ? (
                <Skeleton className="h-[88%] rounded-3xl" />
            ) : (
                <>
                    {!!categoriesData.length && (
                        <>
                            {minWidthMd ? (
                                <CategoryButtons
                                    categoryData={categoriesData}
                                    customButton={customNewShopsFilter}
                                    showFilterNew={showFilterNew}
                                    data-test-id={
                                        dataTestId && `${dataTestId}-categories`
                                    }
                                    className="hidden md:flex"
                                />
                            ) : (
                                <CategoryButtonsSlider
                                    categoryData={categoriesData}
                                    customButton={customNewShopsFilter}
                                    showFilterNew={showFilterNew}
                                    data-test-id={
                                        dataTestId && `${dataTestId}-categories`
                                    }
                                    className="block md:hidden"
                                />
                            )}
                        </>
                    )}

                    <div className="w-full py-8">
                        {searchBarHeaderText ? (
                            <ContainerHeaderText
                                headerText={searchBarHeaderText}
                                data-test-id={
                                    dataTestId && `${dataTestId}-searchBar-text`
                                }
                            />
                        ) : null}

                        <SearchBar
                            inputProps={{
                                placeholder: searchBarPlaceholder,
                                handleClearValue: () =>
                                    setCurrentSearchText(''),
                                'data-test-id':
                                    dataTestId && `${dataTestId}-searchBar`,
                                onChange: event =>
                                    setCurrentSearchText(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    ),
                            }}
                        />
                    </div>

                    {shopsTilesLoading ? (
                        <div className="grid w-full grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                            {Array.from({ length: 20 }).map((_, index) => (
                                <Skeleton
                                    key={`Skeleton card shopsearch ${index}`}
                                    className="h-[88%] rounded-3xl"
                                />
                            ))}
                        </div>
                    ) : (
                        <TilesGrid
                            tilesData={partnersListData}
                            shopLinkPlacement={shopLinkPlacement}
                            ctaText={ctaText}
                            isCtaVisible={
                                partnersListCounter !== partnersListData.length
                            }
                            onCtaClick={handlePartnersLoad}
                            emptyListMessage={emptyListMessage}
                            isShopListEmpty={isShopListEmpty}
                            data-test-id={dataTestId && `${dataTestId}-tiles`}
                        />
                    )}

                    {shouldContentTextRender && (
                        <ContentText
                            textConfig={
                                descriptionData?.description as ContentTextProps['textConfig']
                            }
                            title={descriptionData?.title}
                            subtitle={descriptionData?.subtitle}
                            className="flex flex-col gap-y-6 pt-16"
                        />
                    )}
                </>
            )}
        </Section>
    ) : null;
};

export default SectionShopSearch;
